import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Flag, RefreshCw } from 'lucide-react';
import { REPORT_REASONS } from './ReportUserDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ['pending', 'investigating', 'resolved', 'dismissed'];
const reasonLabel = (v) => REPORT_REASONS.find((r) => r.value === v)?.label || v;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const hoursLeft = (createdAt) => {
  const due = new Date(createdAt).getTime() + 24 * 3600 * 1000;
  return Math.round((due - Date.now()) / 3600000);
};

const ReportRow = ({ r, onUpdate }) => {
  const h = hoursLeft(r.created_at);
  const open = r.status === 'pending' || r.status === 'investigating';
  return (
    <tr className="border-b border-white/10 align-top" data-testid={`admin-report-row-${r.report_id}`}>
      <td className="py-3 px-3 text-white/90">
        <div className="font-semibold">{r.reported_user?.name || 'Deleted user'}</div>
        <div className="text-xs text-white/50">{r.reported_user?.email}</div>
      </td>
      <td className="py-3 px-3 text-white/80 text-sm">{r.reporter?.name || '—'}</td>
      <td className="py-3 px-3 text-sm">
        <div className="text-white">{reasonLabel(r.reason)}</div>
        {r.details && <div className="text-xs text-white/60 mt-1 max-w-xs break-words">"{r.details}"</div>}
      </td>
      <td className="py-3 px-3 text-sm text-white/80">P{r.priority}</td>
      <td className="py-3 px-3 text-sm">
        {open ? (
          <span className={h < 0 ? 'text-red-300 font-semibold' : 'text-amber-200'} data-testid="admin-report-sla">
            {h < 0 ? `Overdue ${-h}h` : `${h}h left`}
          </span>
        ) : <span className="text-white/50 capitalize">{r.status}</span>}
      </td>
      <td className="py-3 px-3">
        {open && (
          <div className="flex flex-wrap gap-2 justify-end">
            {r.status === 'pending' && (
              <button onClick={() => onUpdate(r.report_id, 'investigating')} data-testid={`admin-report-investigate-${r.report_id}`}
                className="px-3 py-1 rounded-lg text-xs bg-blue-500/20 text-blue-200 hover:bg-blue-500/30">Investigate</button>
            )}
            <button onClick={() => onUpdate(r.report_id, 'resolved')} data-testid={`admin-report-resolve-${r.report_id}`}
              className="px-3 py-1 rounded-lg text-xs bg-green-500/20 text-green-200 hover:bg-green-500/30">Resolve</button>
            <button onClick={() => onUpdate(r.report_id, 'dismissed')} data-testid={`admin-report-dismiss-${r.report_id}`}
              className="px-3 py-1 rounded-lg text-xs bg-white/10 text-white/80 hover:bg-white/20">Dismiss</button>
          </div>
        )}
      </td>
    </tr>
  );
};

export const AdminReports = () => {
  const [filter, setFilter] = useState('pending');
  const [data, setData] = useState({ reports: [], counts: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reports`, { ...authHeaders(), params: { status: filter } });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, status) => {
    await axios.patch(`${API}/admin/reports/${id}`, { status }, authHeaders());
    load();
  };

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 mb-8" data-testid="admin-reports-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-white flex items-center"><Flag className="h-6 w-6 mr-2 text-rose-400" />Safety Reports</h2>
        <button onClick={load} data-testid="admin-reports-refresh-btn" className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg flex items-center text-sm">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`admin-reports-filter-${f}`}
            className={`px-4 py-1.5 rounded-full text-sm capitalize transition-colors ${filter === f ? 'bg-rose-500 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}>
            {f} <span className="opacity-70">({data.counts[f] ?? 0})</span>
          </button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/20 text-left text-white/60 text-sm">
              <th className="py-2 px-3">Reported</th><th className="py-2 px-3">By</th><th className="py-2 px-3">Reason</th>
              <th className="py-2 px-3">Priority</th><th className="py-2 px-3">24h review</th><th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.reports.map((r) => <ReportRow key={r.report_id} r={r} onUpdate={update} />)}
          </tbody>
        </table>
        {!loading && data.reports.length === 0 && (
          <p className="text-white/60 text-center py-8" data-testid="admin-reports-empty">No {filter} reports.</p>
        )}
      </div>
    </div>
  );
};

export default AdminReports;
